import {clsx} from 'clsx';
import type {
  CSSProperties,
  FocusEvent,
  PropsWithChildren,
  RefObject,
} from 'react';
import {memo, useCallback, useLayoutEffect, useRef} from 'react';
import {createPortal} from 'react-dom';

import {debounce} from '#utils/debounce.js';

import * as styles from './style.module.scss';

type MenuProps = {
  style?: CSSProperties;
  className?: string;
  isHidden: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
  portalContainer?: HTMLElement | null;
  onBlur: () => void;
};

// In pixels
const ANCHOR_MENU_GAP = 8;
const WINDOW_MENU_GAP = 24;
const MENU_MIN_WIDTH = 200;
const MENU_MAX_HEIGHT = 420;

const useOnClickOutside = (
  ref: RefObject<HTMLDivElement | null>,
  handler: (evt: MouseEvent) => void
) => {
  useLayoutEffect(() => {
    const listener = (evt: MouseEvent) => {
      if (ref.current && !ref.current.contains(evt.target as Node)) {
        handler(evt);
      }
    };

    document.addEventListener('mousedown', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
    };
  }, [ref, handler]);
};

const Menu = memo(function Menu({
  style,
  className,
  children,
  isHidden,
  anchorRef,
  portalContainer,
  onBlur,
}: PropsWithChildren<MenuProps>) {
  const ref = useRef<HTMLDivElement>(null);
  const menuStyle = portalContainer
    ? ({...style, position: 'absolute'} satisfies CSSProperties)
    : style;
  const classes = clsx(styles.menu, className, {
    [styles.hidden]: isHidden,
  });
  const handleOnBlur = useCallback(
    (evt: FocusEvent<HTMLDivElement>) => {
      const relatedTargetId = evt.relatedTarget?.id;

      if (
        ref.current &&
        !ref.current.contains(evt.relatedTarget) &&
        (!relatedTargetId || !relatedTargetId.includes('toggle-button'))
      ) {
        onBlur();
      }
    },
    [ref, onBlur]
  );
  const handleClickOutside = useCallback(
    (evt: MouseEvent) => {
      const target = evt.target;
      const isElement = target instanceof HTMLElement;
      const clickedAnchor = isElement
        ? anchorRef.current?.contains(target)
        : false;
      const isToggleButton = isElement && target.id.includes('toggle-button');

      if (!isToggleButton && !clickedAnchor) {
        onBlur();
      }
    },
    [anchorRef, onBlur]
  );

  useLayoutEffect(() => {
    const sizeAndPosition = (anchor: HTMLDivElement, menu: HTMLDivElement) => {
      const anchorBox = anchor.getBoundingClientRect();
      const offsetParent =
        portalContainer && menu.offsetParent instanceof HTMLElement
          ? menu.offsetParent
          : null;
      const offsetParentBox = offsetParent?.getBoundingClientRect();
      const offsetLeft =
        (offsetParentBox?.left ?? 0) + (offsetParent?.clientLeft ?? 0);
      const offsetTop =
        (offsetParentBox?.top ?? 0) + (offsetParent?.clientTop ?? 0);
      const spaceAbove = anchorBox.top;
      const spaceBelow = window.innerHeight - anchorBox.bottom;
      const availableSpace = Math.max(spaceAbove, spaceBelow);
      const anchorAbove = spaceAbove > spaceBelow;
      const maxHeight = Math.min(
        MENU_MAX_HEIGHT,
        availableSpace - WINDOW_MENU_GAP
      );

      menu.style.maxHeight = `${maxHeight}px`;
      menu.style.minWidth = `${Math.max(MENU_MIN_WIDTH, anchorBox.width)}px`;
      menu.style.left = `${anchorBox.left - offsetLeft}px`;
      menu.style.top = `${anchorBox.bottom - offsetTop + ANCHOR_MENU_GAP}px`;

      if (anchorAbove) {
        menu.style.transform = `translateY(calc(-100% - ${2 * ANCHOR_MENU_GAP}px - ${anchorBox.height}px))`;
      } else {
        menu.style.transform = 'none';
      }
    };
    const handleOnScroll = debounce(
      () => {
        if (!isHidden && anchorRef.current && ref.current) {
          sizeAndPosition(anchorRef.current, ref.current);
        }
      },
      100,
      {maxWait: 50}
    );

    if (!isHidden && anchorRef.current && ref.current) {
      sizeAndPosition(anchorRef.current, ref.current);
    }

    const scrollTarget = portalContainer ?? window;
    scrollTarget.addEventListener('scroll', handleOnScroll, true);

    return () => {
      scrollTarget.removeEventListener('scroll', handleOnScroll, true);
    };
  }, [isHidden, anchorRef, portalContainer]);

  useOnClickOutside(ref, handleClickOutside);

  return createPortal(
    <div ref={ref} style={menuStyle} className={classes} onBlur={handleOnBlur}>
      {children}
    </div>,
    portalContainer ?? document.body
  );
});

export {Menu};
